// packages import
import { Box, Chip, Stack, TableBody, TableCell, TableHead, TableRow, Tooltip, Typography } from "@mui/material"
import moment from "moment"
import { FC, useContext, useEffect, useState } from "react"
import { Link } from "react-router-dom"
// component import
import { CustomPagination } from "../common/CustomPagination"
import { EmptyData } from "../common/EmptyData"
import { StickyTable } from "../common/StickyTable"
import { CourtSkeleton } from "../skeletons/CourtSkeleton"
// others
import { Case, CaseStatus, EventType, ExternalUserRoleRole, Maybe, OrderCaseBy, useFetchAllCasesLazyQuery, UserRole } from "../../../generated"
import { CASES_AGAINST_COURT_HEADER, GRAPHQL_QUERY_POLICY, MAX_CASES_PAGE_LIMIT, STANDARD_DATE_FORMAT, TIME_WITH_SECONDS_FORMAT } from "../../constants"
import { AppContext, AuthContext } from "../../context"
import { ActionType } from "../../context/AppContextReducer"
import { CaseAgainstCourtProps } from "../../interfaceTypes"
import { flexCenter } from "../../theme/styleConstants"
import { capitalizeCase, titleize } from "../../utils"
import { CustomTabButton } from "../../theme/styleComponents"

export const CasesAgainstCourtListing: FC<CaseAgainstCourtProps> = ({ courtId }) => {
  const { currentUser, isAdmin } = useContext(AuthContext)
  const { id: userId } = currentUser || {}
  const [casesData, setCasesData] = useState<(Maybe<Case> | undefined)[]>([])
  const { currentPage, pageLimit, dispatch: reducerDispatch } = useContext(AppContext)
  const [caseFilter, setCaseFilter] = useState<CaseStatus>(CaseStatus.Active)

  const [fetchCases, { loading }] = useFetchAllCasesLazyQuery({
    ...GRAPHQL_QUERY_POLICY,
    variables: undefined,

    onError() {
      setCasesData([])
      reducerDispatch({ type: ActionType.SET_IS_LOADING, isLoading: false })
    },

    onCompleted(data) {
      if (data) {
        const { fetchAllCases } = data;

        if (fetchAllCases) {
          const { cases, pagination } = fetchAllCases

          if (cases) {
            setCasesData(cases)
          }

          if (pagination) {
            const { totalPages, totalCount } = pagination
            totalPages && reducerDispatch({ type: ActionType.SET_TOTAL_PAGE_COUNT, totalPageCount: totalPages });
            totalCount && reducerDispatch({ type: ActionType.SET_TOTAL_DATA_COUNT, totalDataCount: totalCount })
            reducerDispatch({ type: ActionType.SET_IS_LOADING, isLoading: false })
          }
        }
      }
    }
  });

  useEffect(() => {
    fetchCases({
      variables: {
        casesInput: {
          courtId,
          userId: isAdmin ? undefined : userId,
          orderBy: OrderCaseBy.MatterNo,
          status: caseFilter,
          paginationOptions: {
            limit: Math.min(pageLimit, MAX_CASES_PAGE_LIMIT),
            page: currentPage
          }
        }
      }
    })
    // eslint-disable-next-line
  }, [currentPage, userId, caseFilter])

  const isEmpty = casesData?.length === 0 && !loading

  return (
    <>
      <Box display='flex' justifyContent='space-between' alignItems='center' mb={2} height='44px'>
        <Typography fontSize='20px'>
          All assigned cases to court
        </Typography>

        <Box paddingBottom="15px">
          <CustomTabButton
            isLeftItem
            onClick={() => {
              setCaseFilter(CaseStatus.Active)
              reducerDispatch({ type: ActionType.SET_CURRENT_PAGE, currentPage: 1 })
            }}
            className={caseFilter === CaseStatus.Active ? "MuiTab-Custom Mui-selected" : "MuiTab-Custom"}
          >
            Active Cases
          </CustomTabButton>

          <CustomTabButton
            onClick={() => {
              setCaseFilter(CaseStatus.Completed)
              reducerDispatch({ type: ActionType.SET_CURRENT_PAGE, currentPage: 1 })

            }}
            className={caseFilter === CaseStatus.Completed ? "MuiTab-Custom Mui-selected" : "MuiTab-Custom"}
          >
            Closed Cases
          </CustomTabButton>
        </Box>
      </Box>

      {isEmpty ?
        <Box sx={{ ...flexCenter, height: 'calc(100vh - 328px)' }}>
          <EmptyData title='No assigned cases to this court' />
        </Box>
        :
        <StickyTable height='400px'
          pagination={<CustomPagination loading={loading || false} />}
        >
          <TableHead>
            <TableRow>
              {CASES_AGAINST_COURT_HEADER.map((item, index) => {
                const lastIndex = CASES_AGAINST_COURT_HEADER.length - 1 === index

                return (
                  <TableCell component="th" key={`${item}-${index}`} sx={{ minWidth: lastIndex ? '110px' : '150px' }}>
                    {item}
                  </TableCell>
                )
              })}
            </TableRow>
          </TableHead>

          {loading ?
            <CourtSkeleton noOfRows={8} />
            :
            <TableBody>
              {casesData?.map(item => {
                const { caseNumber, caseToExternalUserToExternalUserRole, caseType, id, title, caseEvents, status, caseToUsers, updatedAt } = item || {}
                const { name: caseTypeName } = caseType || {}
                const courtDateType = caseEvents?.filter((date) => date?.eventType === EventType.CourtDate)
                const clientInfo = caseToExternalUserToExternalUserRole?.find((item) => item?.externalUserToExternalUserRole?.externalUser?.externalUserRoles &&
                  item?.externalUserToExternalUserRole?.externalUser?.externalUserRoles[0]?.externalUserRole?.role === ExternalUserRoleRole.Client)?.externalUserToExternalUserRole.externalUser;
                const { firstName, lastName } = clientInfo || {}
                const clientFullName = `${firstName || '-'} ${lastName || '-'}`
                const sortedEvents = courtDateType?.slice().sort(function (firstEvent, secondEvent) {
                  const firstEventHearingAt = moment(firstEvent!.hearingAt!);
                  const secondEventHearingAt = moment(secondEvent!.hearingAt!);
                  return firstEventHearingAt!.date() - secondEventHearingAt!.date();
                });
                const recentHearingAt = sortedEvents && sortedEvents[0]?.hearingAt
                const attorney = caseToUsers?.filter((attorneyUser) => attorneyUser?.user?.roles && attorneyUser?.user?.roles[0]?.role === UserRole.Attorney)
                const paralegal = caseToUsers?.filter((paralegalUser) => paralegalUser?.user?.roles && paralegalUser?.user?.roles[0]?.role === UserRole.Paralegal && paralegalUser?.userType !== "CREATOR")
                const caseStatus = status === CaseStatus.Completed ? 'Closed' : 'Active'

                return (
                  <TableRow key={id} hover>
                    <TableCell>
                      <Box maxWidth='120px'>
                        <Link to={`/matters/matter-detail/${id}`}>
                          <Tooltip title={caseNumber || '--'} arrow>
                            <Typography variant="body1" noWrap fontWeight='normal' color='primary.main'>
                              {("#" + caseNumber) || '--'}
                            </Typography>
                          </Tooltip>
                        </Link>
                      </Box>
                    </TableCell>

                    <TableCell>
                      <Tooltip title={title || '--'} arrow>
                        <Typography variant="body1" noWrap maxWidth='150px'>
                          {title || '--'}
                        </Typography>
                      </Tooltip>
                    </TableCell>

                    <TableCell>
                      <Tooltip title={caseTypeName || '--'} arrow>
                        <Typography variant="body1" textTransform='capitalize' noWrap maxWidth='120px'>
                          {caseTypeName || '--'}
                        </Typography>
                      </Tooltip>
                    </TableCell>

                    <TableCell>
                      <Tooltip title={clientFullName || '--'} arrow>
                        <Typography variant="body1" noWrap maxWidth='150px' fontWeight={400}>
                          {clientFullName}
                        </Typography>
                      </Tooltip>
                    </TableCell>

                    <TableCell>
                      {titleize(caseStatus || '')}
                    </TableCell>

                    <TableCell>
                      {paralegal?.length === 0
                        ?
                        <Typography>{"--"}</Typography>
                        :
                        <>
                          <Stack direction='row' flexWrap='wrap' gap='10px'>
                            {paralegal?.slice(0, 2).map((assignedUser) => {
                              const { user } = assignedUser || {}
                              const { firstName, lastName } = user || {}
                              const userFullName = `${firstName} ${lastName}`

                              return (
                                <Chip key={id} label={capitalizeCase(userFullName || '')} variant="outlined" />
                              )
                            })}
                          </Stack>

                          {paralegal?.length! > 2 &&
                            <Tooltip
                              title={paralegal?.slice(2)?.map((assignedUser) => {
                                const { user } = assignedUser || {}
                                const { firstName, lastName } = user || {}
                                const userFullName = `${firstName} ${lastName}`

                                return (
                                  <Typography color='white'>{capitalizeCase(userFullName || '')}</Typography>
                                )
                              })}
                              sx={{ gap: '10px' }}
                              placement="bottom-start"
                            >
                              <Box pt={1}>
                                <Chip key={id} label={`+${paralegal?.length && (paralegal?.length - 2)}`} color="primary" variant="outlined" />
                              </Box>
                            </Tooltip>
                          }
                        </>
                      }
                    </TableCell>

                    <TableCell>
                      {attorney?.length === 0
                        ?
                        <Typography>--</Typography>
                        :
                        <>
                          <Stack direction='row' flexWrap='wrap' gap='10px'>
                            {attorney?.slice(0, 2).map((assignedUser) => {
                              const { user, id } = assignedUser || {}
                              const { firstName, lastName } = user || {}
                              const userFullName = `${firstName} ${lastName}`

                              return (
                                <Chip key={id} label={capitalizeCase(userFullName || '')} variant="filled" />
                              )
                            })}
                          </Stack>

                          {attorney?.length! > 2 &&
                            <Tooltip
                              title={attorney?.slice(2)?.map((assignedUser) => {
                                const { user } = assignedUser || {}
                                const { firstName, lastName } = user || {}
                                const userFullName = `${firstName} ${lastName}`

                                return (
                                  <Typography color='white'>{capitalizeCase(userFullName || '')}</Typography>
                                )
                              })}
                              sx={{ gap: '10px' }}
                              placement="bottom-start"
                            >
                              <Box pt={1}>
                                <Chip key={id} label={`+${attorney?.length && (attorney?.length - 2)}`} color="secondary" variant="outlined" />
                              </Box>
                            </Tooltip>
                          }
                        </>
                      }
                    </TableCell>

                    <TableCell>
                      <Typography variant="body2" noWrap maxWidth='150px' fontWeight={400}>
                        {recentHearingAt ? moment(parseInt(recentHearingAt || '')).format(STANDARD_DATE_FORMAT) : '--'}
                      </Typography>
                    </TableCell>

                    <TableCell>
                      <Typography variant="body2" noWrap maxWidth='150px' fontWeight={400}>
                        {updatedAt ? moment(parseInt(updatedAt)).format(STANDARD_DATE_FORMAT) : '--'}
                      </Typography>
                      <Typography noWrap color='wardGray' fontSize="14px">
                        {updatedAt ? moment(parseInt(updatedAt)).format(TIME_WITH_SECONDS_FORMAT) : '--'}
                      </Typography>
                    </TableCell>
                  </TableRow>
                )
              })}
            </TableBody>
          }
        </StickyTable>
      }
    </>
  )
}